import { formatNumber } from '../billing/numbering.js';

/**
 * Offline billing rules shared by the kiosk (which bills offline) and the server (which checks
 * the bills when they sync). Spec §3 and §4.10:
 *  - only registered billing devices, holding a signed offline licence token from the server;
 *  - at most N hours since the last successful sync (platform setting, 72 h) and never past the
 *    subscription's expiry date;
 *  - decisions use server time: the device measures elapsed time from the token's issue time
 *    (server clock) and refuses if its own clock has moved backwards.
 *  - offline bills use a per-device invoice series ({DEVICE}/{FY}/{SEQ6}) so numbers never collide.
 */
const CLOCK_SKEW_MS = 5 * 60_000;

/** Token numbers for offline orders: device D007 issues 7001, 7002, … (never clash with online tokens). */
export function offlineTokenNo(deviceCode, n) {
  const device = Number(String(deviceCode).replace(/\D/g, '')) || 0;
  return device * 1000 + (((n - 1) % 999) + 1);
}

export function offlineInvoiceNo(format, { deviceCode, fy, seq }) {
  return formatNumber(format, { deviceCode, fy, seq });
}

// ---------------------------------------------------------------- token encoding (base64url JSON + Ed25519)
const toB64url = (bytes) => {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromB64url = (s) => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

export function encodeTokenPayload(payload) {
  return toB64url(new TextEncoder().encode(JSON.stringify(payload)));
}

/** "<payload>.<signature>" → { payload, signed (bytes that were signed), signature (bytes) } or null. */
export function decodeOfflineToken(token) {
  const [body, sig] = String(token ?? '').split('.');
  if (!body || !sig) return null;
  try {
    return {
      payload: JSON.parse(new TextDecoder().decode(fromB64url(body))),
      signed: new TextEncoder().encode(body),
      signature: fromB64url(sig),
    };
  } catch {
    return null;
  }
}

export const signatureToB64url = toB64url;

/**
 * Can this device bill offline right now?
 * token: { iat, exp } (server ms); localAtIssue: device Date.now() when the token arrived.
 * Returns { ok, reason?, serverNow, remainingMs }.
 */
export function offlineAllowance(token, { localAtIssue, now = Date.now() }) {
  if (!token?.iat || !token?.exp) return { ok: false, reason: 'NO_TOKEN', serverNow: null, remainingMs: 0 };
  if (now + CLOCK_SKEW_MS < localAtIssue)
    return { ok: false, reason: 'CLOCK_CHANGED', serverNow: null, remainingMs: 0 };
  const serverNow = token.iat + Math.max(0, now - localAtIssue);
  const remainingMs = token.exp - serverNow;
  if (remainingMs <= 0) return { ok: false, reason: 'EXPIRED', serverNow, remainingMs: 0 };
  return { ok: true, serverNow, remainingMs };
}

export const OFFLINE_REASONS = Object.freeze({
  NO_TOKEN: 'This device has no offline permit yet. Connect once to download it.',
  CLOCK_CHANGED: 'The device clock was changed. Connect to the internet to continue billing.',
  EXPIRED: 'Offline billing time is used up. Connect to the internet to sync and continue.',
  NOT_DEVICE: 'Only registered billing devices can bill offline.',
  DISABLED: 'Offline billing is switched off in Settings → Offline & displays.',
});
